import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { COMPANY, LOGOS, SERVICES, SOCIALS } from "@/data/site";
import CPMark from "@/components/CPMark";

const SOCIAL_ICON = { linkedin: Linkedin, instagram: Instagram, facebook: Facebook };

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-navy text-white relative overflow-hidden">
      <div className="grain absolute inset-0 opacity-30" />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <CPMark className="w-24 h-24" variant="dark" />
              <span className="font-display font-800 text-lg tracking-tightest">
                CONSILIUM <span className="text-gold">PRO</span>
              </span>
            </div>
            <p className="text-white/70 font-body text-sm leading-relaxed max-w-xs">
              {COMPANY.tagline}
            </p>
            <p className="text-white/50 font-body text-sm leading-relaxed max-w-xs mt-4">
              Strengthening organizations through strategic workforce operations, compliance-driven people solutions, and mission-focused consulting.
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="overline text-gold mb-6">Services</h4>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} data-testid={`footer-service-${s.slug}`} className="text-white/70 text-sm hover:text-gold transition-colors">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="overline text-gold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[["About", "/about"], ["Our Story", "/our-story"], ["Leadership", "/team"], ["Industries", "/industries"], ["Blog", "/blog"], ["Resources", "/resources"], ["Talent Network", "/talent-network"], ["Contact", "/contact"]].map(([l, to]) => (
                <li key={to}><Link to={to} className="text-white/70 text-sm hover:text-gold transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="overline text-gold mb-6">Contact</h4>
            <ul className="space-y-4 text-sm text-white/70">
              <li><a href={COMPANY.phoneHref} className="flex items-center gap-3 hover:text-gold transition-colors"><Phone className="w-4 h-4 text-gold" strokeWidth={1.5} /> {COMPANY.phone}</a></li>
              <li><a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 hover:text-gold transition-colors"><Mail className="w-4 h-4 text-gold" strokeWidth={1.5} /> {COMPANY.email}</a></li>
              <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-gold" strokeWidth={1.5} /> {COMPANY.hq}</li>
            </ul>
            <div className="flex items-center gap-3 mt-6">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICON[s.icon];
                return (
                  <a
                    key={s.icon}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    data-testid={`footer-social-${s.icon}`}
                    className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy transition-colors"
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                );
              })}
            </div>
            <Link to="/contact" data-testid="footer-cta" className="mt-8 inline-flex items-center gap-2 text-gold font-600 text-sm group">
              Schedule a Consultation <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 text-white/50 text-xs">
          <p>&copy; {year} {COMPANY.legalName}. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-gold transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gold transition-colors">Terms of Use</Link>
            <Link to="/accessibility" data-testid="footer-accessibility" className="hover:text-gold transition-colors">Accessibility</Link>
            <Link to="/login" data-testid="footer-team-login" className="hover:text-gold transition-colors">Team Login</Link>
            <span>{COMPANY.website}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
